import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowUpRight, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_KEYS = ["offert", "express", "lots", "extra"];

export default function Quote() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    service_type: "offert",
    name: "",
    company: "",
    email: "",
    phone: "",
    pickup: "",
    delivery: "",
    pickup_date: "",
    cargo: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t.quote.error;
      setErrorMsg(typeof msg === "string" ? msg : t.quote.error);
    }
  };

  const reset = () => {
    setForm({
      service_type: "offert",
      name: "",
      company: "",
      email: "",
      phone: "",
      pickup: "",
      delivery: "",
      pickup_date: "",
      cargo: "",
      message: "",
    });
    setStatus("idle");
  };

  return (
    <div data-testid="quote-page" className="pt-28 md:pt-36 pb-16">
      <section className="px-6 md:px-12 lg:px-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-4">
          / 04 — {t.quote.title}
        </div>
        <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-none">
          {t.quote.subtitle}
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg">
          {t.quote.lead}
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-16 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="border border-zinc-800 p-8 bg-[#0d0d10] sticky top-28">
              <div className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 mb-4">
                {t.contact.title}
              </div>
              <ul className="space-y-4 text-sm">
                <li className="border-b border-zinc-800 pb-3">
                  <div className="text-zinc-500 text-xs uppercase tracking-widest mb-1">
                    {t.team.roles.chef}
                  </div>
                  <div className="text-white">Michael Bränninger</div>
                  <a
                    href="tel:0732008960"
                    className="text-[#ff5a00] hover:underline text-sm"
                  >
                    0732 00 89 60
                  </a>
                </li>
                <li className="border-b border-zinc-800 pb-3">
                  <div className="text-zinc-500 text-xs uppercase tracking-widest mb-1">
                    {t.team.roles.hr}
                  </div>
                  <div className="text-white">Mattias Arlefur Bränninger</div>
                  <a
                    href="tel:0733008901"
                    className="text-[#ff5a00] hover:underline text-sm"
                  >
                    0733 00 89 01
                  </a>
                </li>
                <li>
                  <div className="text-zinc-500 text-xs uppercase tracking-widest mb-1">
                    {t.team.roles.forman}
                  </div>
                  <div className="text-white">Björn Persson</div>
                  <a
                    href="tel:0732008962"
                    className="text-[#ff5a00] hover:underline text-sm"
                  >
                    0732 00 89 62
                  </a>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  {t.contact.hours}
                </div>
                <div className="font-display text-3xl text-[#ff5a00]">24/7</div>
              </div>
              <div className="mt-6 pt-6 border-t border-zinc-800 text-sm text-zinc-400 space-y-1">
                <div>{t.contact.address}</div>
                <div className="text-xs font-mono tracking-widest text-zinc-500 mt-2">
                  {t.contact.org_label} 556712-0208
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 order-1 lg:order-2">
            {status === "success" ? (
              <div
                data-testid="quote-success"
                className="border border-[#ff5a00] bg-[#ff5a00]/5 p-10 md:p-14"
              >
                <Check className="w-12 h-12 text-[#ff5a00] mb-6" strokeWidth={1.5} />
                <h2 className="font-display text-4xl md:text-5xl uppercase">
                  {t.quote.success_title}
                </h2>
                <p className="mt-4 text-zinc-300 max-w-lg">
                  {t.quote.success_msg}
                </p>
                <button
                  onClick={reset}
                  data-testid="quote-reset-btn"
                  className="mt-8 inline-flex items-center gap-3 border border-zinc-700 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
                >
                  {t.quote.new_quote} <ArrowUpRight size={14} />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="quote-form">
                {/* Service type */}
                <div>
                  <div className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 mb-4">
                    {t.quote.service_label} *
                  </div>
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 border border-zinc-800"
                    data-testid="quote-form-service-select"
                  >
                    {SERVICE_KEYS.map((k, i) => {
                      const selected = form.service_type === k;
                      return (
                        <button
                          type="button"
                          key={k}
                          data-testid={`service-option-${k}`}
                          onClick={() => setForm({ ...form, service_type: k })}
                          className={`p-4 md:p-5 text-left border-zinc-800 transition-colors ${
                            i !== 0 ? "border-l" : ""
                          } ${
                            selected
                              ? "bg-[#ff5a00] text-black"
                              : "text-zinc-300 hover:bg-zinc-900"
                          }`}
                        >
                          <div className="text-[10px] font-mono tracking-widest opacity-70">
                            0{i + 1}
                          </div>
                          <div className="font-display text-xl md:text-2xl uppercase mt-1">
                            {t.quote.services[k]}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Contact fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  <Field
                    label={`${t.quote.fields.name} *`}
                    testId="quote-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                  <Field
                    label={t.quote.fields.company}
                    testId="quote-company"
                    value={form.company}
                    onChange={update("company")}
                  />
                  <Field
                    label={`${t.quote.fields.email} *`}
                    testId="quote-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                  <Field
                    label={`${t.quote.fields.phone} *`}
                    testId="quote-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                  />
                  <Field
                    label={t.quote.fields.pickup}
                    testId="quote-pickup"
                    value={form.pickup}
                    onChange={update("pickup")}
                  />
                  <Field
                    label={t.quote.fields.delivery}
                    testId="quote-delivery"
                    value={form.delivery}
                    onChange={update("delivery")}
                  />
                  <Field
                    label={t.quote.fields.date}
                    testId="quote-date"
                    type="date"
                    value={form.pickup_date}
                    onChange={update("pickup_date")}
                  />
                  <Field
                    label={t.quote.fields.cargo}
                    testId="quote-cargo"
                    value={form.cargo}
                    onChange={update("cargo")}
                  />
                </div>

                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500">
                    {t.quote.fields.message}
                  </label>
                  <textarea
                    data-testid="quote-message"
                    className="brut-input mt-2"
                    value={form.message}
                    onChange={update("message")}
                  />
                </div>

                {status === "error" && (
                  <div
                    data-testid="quote-error"
                    className="border border-red-600 bg-red-600/10 p-4 flex items-start gap-3 text-sm text-red-300"
                  >
                    <AlertCircle size={18} className="shrink-0 mt-0.5" />
                    {errorMsg}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="quote-form-submit-btn"
                  disabled={status === "loading"}
                  className="group inline-flex items-center gap-4 bg-[#ff5a00] text-black px-8 py-4 text-sm tracking-[0.25em] uppercase font-bold hover:bg-[#ff7a30] transition-colors disabled:opacity-60"
                >
                  {status === "loading" ? t.common.submitting : t.common.submit}
                  <ArrowUpRight
                    size={18}
                    className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                  />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, testId, type = "text", value, onChange, required }) => (
  <div className="py-2">
    <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
      {label}
    </label>
    <input
      type={type}
      data-testid={testId}
      className="brut-input"
      value={value}
      onChange={onChange}
      required={required}
    />
  </div>
);
